import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';

const NotFound = () => {
  const location = useLocation();

  // Best-effort signal to crawlers / bots that this URL is invalid.
  // Returning an actual HTTP 404 status from a Vite SPA on Vercel requires
  // a server-side catch-all (Vercel function or rewrite to a static 404.html);
  // tracked separately. This component covers the user-facing UX. (MLB-001)
  useEffect(() => {
    document.title = '404: Sabotaged | Sabotage Fantasy';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    if (import.meta.env.DEV) {
      console.warn('404: no route matched', location.pathname);
    }
    return () => { document.head.removeChild(meta); };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-dark flex flex-col items-center justify-center px-4 gap-6 text-center">
      <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-20 w-auto opacity-80" />
      <div>
        <p className="text-7xl font-display font-bold text-primary leading-none">404</p>
        <h1 className="mt-4 text-2xl font-display font-bold text-foreground">
          Even your URL got <span className="text-primary">sabotaged</span>.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">
          We couldn't find the page you were looking for. It may have been moved, drafted by another team, or never existed in the first place.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
        {/* SAB-587's fix, applied to the 404 it missed.
            "Take me home" pointed at /beta -- a hard switch to the MLB shell, from the one
            screen every mistyped URL, dead link and stale route lands on. An NFL user who
            404s was told "take me home" and sent to the wrong sport. /home is the landing
            gate: it runs the fallback chain (live NFL -> in-season MLB -> any NFL ->
            cold-start chooser) so the destination follows the leagues the user actually has,
            and an anonymous visitor is bounced to /auth by ProtectedRoute rather than
            dropped into a sport shell.

            "Browse leagues" stays at "/" deliberately, and was checked rather than assumed:
            for an ANON visitor that is the public landing page, and for an AUTHED one both
            MarketingHome and LandingPage redirect to DEFAULT_AUTHED_HOME -- so it already
            resolves through the same chain and cannot land anyone on the wrong sport. */}
        <Button asChild className="flex-1">
          <Link to={DEFAULT_AUTHED_HOME}>Take me home</Link>
        </Button>
        <Button asChild variant="outline" className="flex-1">
          <Link to="/">Browse leagues</Link>
        </Button>
      </div>
    </div>
  );
};

export default NotFound;
